import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Bot,
  ChevronLeft,
  ChevronRight,
  Database,
  Eye,
  FileSpreadsheet,
  LayoutPanelTop,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Upload,
} from "lucide-react";
import SortableTable from "../SortableTable";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import TablesSidebar from "./TablesSidebar";
import TableSchemaPanel from "./TableSchemaPanel";
import TableDetailsPanel from "./TableDetailsPanel";
import TableAIQueryPanel from "./TableAIQueryPanel";
import TableAIQueryResults from "./TableAIQueryResults";
import { useTablesAIQuery } from "./TablesAIQueryContext";
import useTablesWorkspaceState from "./useTablesWorkspaceState";
import useTablePageData from "./useTablePageData";

function TablesEmptyState({ onOpenAI }) {
  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="max-w-xl text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300">
          <Database className="h-8 w-8" />
        </div>
        <h2 className="mt-6 text-2xl font-semibold text-gray-900 dark:text-gray-100">
          Select a table to explore your data
        </h2>
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          Browse tables from the left sidebar, switch between data and schema, and use the right panel for row editing or AI-generated SQL.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button onClick={onOpenAI}>
            <Bot className="mr-2 h-4 w-4" />
            Open AI Query
          </Button>
        </div>
      </div>
    </div>
  );
}

function ImportStatus({ uploading, uploadResult, progress, uploadingPreview }) {
  if (!uploading && !uploadResult && !uploadingPreview) {
    return null;
  }
  return (
    <Card className="mt-4 border-dashed">
      <CardContent className="pt-5">
        {uploading && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 text-sm text-gray-700 dark:text-gray-200">
              <span>Import running</span>
              <span>
                {progress.processed} / {progress.total || 0}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
              <div
                className="h-full rounded-full bg-blue-600 transition-all dark:bg-blue-500"
                style={{
                  width: `${Math.round((progress.processed / progress.total) * 100) || 0}%`,
                }}
              />
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Inserted: {progress.inserted} • Updated: {progress.updated} • Errors: {progress.errors}
            </div>
          </div>
        )}
        {uploadingPreview && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Loader2 className="h-4 w-4 animate-spin" />
            Building import preview...
          </div>
        )}
        {uploadResult && (
          <div
            className={`rounded-xl px-4 py-3 text-sm ${uploadResult.status === "success" ? "bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300" : "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"}`}
          >
            {uploadResult.status === "success"
              ? `Import completed: ${uploadResult.inserted} inserted, ${uploadResult.updated} updated, ${uploadResult.errors} errors.`
              : uploadResult.message}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TablesWorkspace() {
  const tablesState = useSelector((state) => state.tables);
  const tables = useMemo(() => tablesState.tables || [], [tablesState.tables]);
  const { displayedEntry, clearDisplayedEntry } = useTablesAIQuery();
  const {
    selectedTableName,
    contentTab,
    setContentTab,
    rightPanelTab,
    setRightPanelTab,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    isRightPanelOpen,
    selectedRow,
    editorMode,
    selectTable,
    openRowDetails,
    openCreateRow,
    openTableDetails,
    openAI,
    closeRightPanel,
  } = useTablesWorkspaceState();

  const selectedTable = useMemo(
    () => tables.find((table) => table.table === selectedTableName) || null,
    [selectedTableName, tables]
  );

  const {
    pageSize,
    tableData,
    displayData,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    searchLoading,
    isSearchActive,
    currentPage,
    pageCount,
    goToPage,
    refresh,
    saveRecord,
    deleteRecord,
    exportExcel,
    importFile,
    previewImport,
    uploading,
    uploadingPreview,
    uploadResult,
    progress,
  } = useTablePageData(selectedTableName);

  const [editorSaving, setEditorSaving] = useState(false);
  const [editorError, setEditorError] = useState(null);
  const fileInputRef = useRef(null);
  const filePreviewInputRef = useRef(null);
  const isAIMode = Boolean(displayedEntry);

  const mapRowToObject = useCallback((row) => {
    const mappedRow = {};
    displayData.columns.forEach((columnName, index) => {
      mappedRow[columnName] = row[index];
    });
    return mappedRow;
  }, [displayData.columns]);

  const tableRows = useMemo(() => {
    return (displayData.rows || []).map((row, index) => {
      const rowData = [...row];
      rowData.originalIndex = index;
      return rowData;
    });
  }, [displayData.rows]);

  const gridColumns = useMemo(() => {
    const leadingColumn = {
      key: "open",
      label: "Edit",
      render: (item) => (
        <button
          onClick={(event) => {
            event.stopPropagation();
            openRowDetails(mapRowToObject(item));
          }}
          className="text-gray-500 transition hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-300"
        >
          <Eye className="h-4 w-4" />
        </button>
      ),
      filterable: false,
      rawValue: () => "",
    };

    const dataColumns = (displayData.columns || []).map((columnName, columnIndex) => ({
      key: columnName,
      label: columnName,
      render: (item) => {
        const value = item[columnIndex];
        if (value === null) {
          return "NULL";
        }
        const foreignKeyNames = displayData.foreign_keys?.[columnName];
        if (foreignKeyNames && foreignKeyNames[value]) {
          return `${value} (${foreignKeyNames[value]})`;
        }
        return typeof value === "string" ? value : JSON.stringify(value);
      },
      rawValue: (item) => {
        const value = item[columnIndex];
        if (value === null) {
          return "";
        }
        return typeof value === "string" ? value : JSON.stringify(value);
      },
    }));

    return [leadingColumn, ...dataColumns];
  }, [displayData.columns, displayData.foreign_keys, mapRowToObject, openRowDetails]);

  const handleSubmitRecord = async (payload, meta) => {
    setEditorSaving(true);
    setEditorError(null);
    try {
      await saveRecord(payload, meta);
      openTableDetails();
    } catch (saveError) {
      setEditorError(saveError.message);
    } finally {
      setEditorSaving(false);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    setEditorSaving(true);
    setEditorError(null);
    try {
      await deleteRecord(recordId);
      openTableDetails();
    } catch (deleteError) {
      setEditorError(deleteError.message);
      throw deleteError;
    } finally {
      setEditorSaving(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full gap-3">
      <TablesSidebar
        tables={tables}
        selectedTableName={selectedTableName}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((current) => !current)}
        onSelectTable={(tableName) => {
          clearDisplayedEntry();
          selectTable(tableName);
        }}
      />

      <section className="flex min-w-0 flex-1 flex-col overflow-hidden border-y border-l border-gray-200 bg-white/95 shadow-sm dark:border-gray-800 dark:bg-gray-900/95">
        {tablesState.loading && tables.length === 0 && !isAIMode ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading tables...
          </div>
        ) : isAIMode ? (
          <>
            <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-800">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-500 dark:text-purple-300">
                    AI Query Assistant
                  </p>
                  <h1 className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    {displayedEntry ? displayedEntry.query : "Ask a question about your data"}
                  </h1>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {displayedEntry
                      ? displayedEntry.status === "success"
                        ? `${displayedEntry.response?.row_count || 0} rows returned in ${displayedEntry.response?.execution_time_ms?.toFixed(1)}ms`
                        : displayedEntry.status === "error"
                          ? "The selected query returned an error."
                          : "The selected query is running."
                      : "Use the right panel to submit a new natural language query and browse the result here."}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {selectedTable && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        clearDisplayedEntry();
                        openTableDetails();
                      }}
                    >
                      <LayoutPanelTop className="mr-2 h-4 w-4" />
                      Details
                    </Button>
                  )}
                  <Button variant="outline" onClick={openAI}>
                    <Bot className="mr-2 h-4 w-4" />
                    AI Query
                  </Button>
                </div>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <TableAIQueryResults activeEntry={displayedEntry} />
            </div>
          </>
        ) : !selectedTable ? (
          <TablesEmptyState onOpenAI={openAI} />
        ) : (
          <>
            <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-800">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
                    Database Table
                  </p>
                  <h1 className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    {selectedTable.table}
                  </h1>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {selectedTable.schema?.title || "Table overview"} • {Number(selectedTable.row_count || tableData.row_count || 0).toLocaleString()} rows • {Object.keys(selectedTable.schema?.properties || {}).length} columns
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" onClick={openTableDetails}>
                    <LayoutPanelTop className="mr-2 h-4 w-4" />
                    Details
                  </Button>
                  <Button variant="outline" onClick={openAI}>
                    <Bot className="mr-2 h-4 w-4" />
                    AI Query
                  </Button>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Button onClick={openCreateRow} disabled={selectedTable.schema?.create_disabled}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Row
                </Button>
                <Button variant="outline" onClick={() => exportExcel(false)}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Export Excel
                </Button>
                <Button variant="outline" onClick={() => exportExcel(true)} disabled={selectedTable.schema?.create_disabled || uploading}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Excel Template
                </Button>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={selectedTable.schema?.create_disabled || uploading}>
                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  Import Excel
                </Button>
                <Button variant="outline" onClick={() => filePreviewInputRef.current?.click()} disabled={selectedTable.schema?.create_disabled || uploadingPreview}>
                  {uploadingPreview ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  Preview Import
                </Button>
                <Button variant="ghost" onClick={refresh}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      importFile(file);
                    }
                    event.target.value = "";
                  }}
                />
                <input
                  ref={filePreviewInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      previewImport(file);
                    }
                    event.target.value = "";
                  }}
                />
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                  <button
                    onClick={() => setContentTab("data")}
                    className={`rounded-lg px-3 py-1.5 text-sm transition ${contentTab === "data" ? "bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-300"}`}
                  >
                    Data
                  </button>
                  <button
                    onClick={() => setContentTab("schema")}
                    className={`rounded-lg px-3 py-1.5 text-sm transition ${contentTab === "schema" ? "bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-300"}`}
                  >
                    Structure
                  </button>
                </div>

                {contentTab === "data" && (
                  <div className="relative w-full max-w-md">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Search in table... (min 3 characters)"
                      className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                    {searchLoading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />}
                  </div>
                )}
              </div>

              <ImportStatus
                uploading={uploading}
                uploadResult={uploadResult}
                progress={progress}
                uploadingPreview={uploadingPreview}
              />
            </div>

            <div className="min-h-0 flex-1 overflow-hidden p-4">
              {contentTab === "schema" ? (
                <div className="h-full overflow-y-auto">
                  <TableSchemaPanel table={selectedTable} />
                </div>
              ) : error ? (
                <div className="flex h-full items-center justify-center text-center text-sm text-red-600 dark:text-red-300">
                  {error}
                </div>
              ) : loading ? (
                <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading table...
                </div>
              ) : (
                <div className="flex h-full flex-col">
                  <div className="min-h-0 flex-1">
                    <SortableTable
                      data={tableRows}
                      columns={gridColumns}
                      emptyMessage="No rows available"
                      countLabel={
                        isSearchActive
                          ? `${tableRows.length} search result${tableRows.length > 1 ? "s" : ""}`
                          : `Page ${currentPage} of ${pageCount} • ${pageSize} rows max`
                      }
                      exportable={true}
                      exportFilename={`${selectedTable.table}-data.csv`}
                      fullHeight={true}
                      className="flex h-full flex-col"
                      onRowClick={(row) => openRowDetails(mapRowToObject(row))}
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {isSearchActive
                        ? "Server search mode: pagination is temporarily disabled."
                        : `Showing ${(currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, tableData.row_count || 0)} of ${Number(tableData.row_count || 0).toLocaleString()} rows`}
                    </div>
                    {!isSearchActive && pageCount > 1 && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="small"
                          disabled={currentPage <= 1}
                          onClick={() => goToPage(currentPage - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          {currentPage} / {pageCount}
                        </div>
                        <Button
                          variant="outline"
                          size="small"
                          disabled={currentPage >= pageCount}
                          onClick={() => goToPage(currentPage + 1)}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {isRightPanelOpen && (
        <aside className="flex h-full w-[420px] shrink-0 flex-col overflow-hidden border-y border-l border-gray-200 bg-white/95 shadow-sm dark:border-gray-800 dark:bg-gray-900/95">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-800">
            <div className="flex items-center gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
              <button
                onClick={() => {
                  clearDisplayedEntry();
                  setRightPanelTab("details");
                  openTableDetails();
                }}
                className={`rounded-lg px-3 py-1.5 text-sm transition ${rightPanelTab === "details" ? "bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-300"}`}
              >
                Details
              </button>
              <button
                onClick={() => {
                  setRightPanelTab("ai");
                  openAI();
                }}
                className={`rounded-lg px-3 py-1.5 text-sm transition ${rightPanelTab === "ai" ? "bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-300"}`}
              >
                AI Query
              </button>
            </div>
            <Button variant="ghost" size="small" onClick={closeRightPanel} className="h-9 w-9 p-0">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="min-h-0 flex-1">
            {rightPanelTab === "ai" ? (
              <TableAIQueryPanel />
            ) : (
              <TableDetailsPanel
                table={selectedTable}
                editorMode={editorMode}
                selectedRow={selectedRow}
                onCreateRow={() => {
                  clearDisplayedEntry();
                  openCreateRow();
                }}
                onCancelEdit={() => {
                  clearDisplayedEntry();
                  openTableDetails();
                }}
                onSubmitRecord={handleSubmitRecord}
                onDeleteRecord={handleDeleteRecord}
                saving={editorSaving}
                error={editorError}
              />
            )}
          </div>
        </aside>
      )}
    </div>
  );
}

export default TablesWorkspace;
